"use client";

import { Heart, Wallet, Gift, ShieldCheck, Zap } from "lucide-react";
import { SiCashapp } from "react-icons/si";
import { ALL_PAYMENT_METHOD_IDS, PAYMENT_METHOD_LABELS, HIDDEN_PAYMENT_METHOD_IDS } from "./PaymentFlow";

// Methods that only show for beta users on their own Add Funds page
// (`!isPublic && isBetaUser` in PaymentFlow). They must never appear in the
// public payment-link preview, where the payer is neither.
const PREVIEW_BETA_ONLY_IDS: readonly string[] = [
  "coinbase-headless", // "Coinbase Pay (Apple Pay / Google Pay)"
  "paypal",
  "venmo",
];

const METHOD_LOGOS: Record<string, React.ReactNode> = {
  "card-bank": (
    <div className="flex flex-col gap-0.5">
      <div className="flex items-center gap-0.5">
        <img src="/Visa-logo.png" alt="Visa" className="h-3 rounded bg-white px-0.5" />
        <img src="/mastercard-logo.png" alt="MC" className="h-3 rounded" />
        <img src="/amex.png" alt="Amex" className="h-3.5 rounded bg-white px-0.5" />
        <img src="/discover.jpg" alt="Discover" className="h-3 rounded bg-white px-0.5" />
      </div>
      <div className="flex items-center gap-0.5 text-[8px] text-gray-500">
        <span>Powered by</span>
        <img src="/Stripe_Logo.png" alt="Stripe" className="h-2.5 rounded-full" />
      </div>
    </div>
  ),
  "coinbase-onramp": (
    <div className="flex items-center gap-0.5">
      <img src="/Visa-logo.png" alt="Visa" className="h-3 rounded bg-white px-0.5" />
      <img src="/mastercard-logo.png" alt="MC" className="h-3 rounded bg-white px-0.5" />
      <img src="/applepay-logo.png" alt="Apple Pay" className="h-3 rounded bg-white px-0.5" />
      <img src="/googlepay-logo.png" alt="Google Pay" className="h-3 rounded bg-white px-0.5" />
    </div>
  ),
  "cashapp": (
    <div className="flex items-center gap-0.5">
      <img src="/CashApp White BG.png" alt="CashApp" className="h-4 w-4 rounded-full" />
      <img src="/venmo.png" alt="Venmo" className="h-3.5 w-3.5 rounded-full bg-white p-0.5" />
      <img src="/zelle.png" alt="Zelle" className="h-3.5 w-3.5 rounded-full bg-white p-0.5" />
      <img src="/revoult.png" alt="Revolut" className="h-3.5 w-3.5 rounded-full bg-white p-0.5" />
      <img src="/wise.png" alt="Wise" className="h-3.5 w-3.5 rounded-full bg-white/90 p-0.5" />
    </div>
  ),
  "wallet": (
    <div className="flex items-center justify-center w-5 h-5 rounded bg-[#61CE70]/15">
      <Wallet size={10} className="text-[#61CE70]" />
    </div>
  ),
  // Both Lightning ids ("CashApp Pay" + "Add Money via Lightning") are the
  // same backend; surface a CashApp-green mark so the rows aren't icon-less.
  "lightning-cashapp": (
    <div className="flex items-center justify-center w-5 h-5 rounded-full bg-white">
      <SiCashapp className="w-4 h-4 text-[#00D632]" />
    </div>
  ),
  "lightning-btc": (
    <div className="flex items-center justify-center w-5 h-5 rounded bg-[#00D632]/15">
      <Zap size={10} className="text-[#00D632]" fill="currentColor" />
    </div>
  ),
};

// Per-method label overrides for the preview rows. The Coinbase card now leads
// with its card-network logos, so its row reads "Instant and secure payment"
// instead of the bare "Coinbase" label used elsewhere in the app.
const METHOD_LABEL_OVERRIDES: Record<string, string> = {
  "coinbase-onramp": "Instant and secure payment",
};

export interface UniversalLinkConfig {
  // Always "direct" now — the page goes straight to a minimal payment card.
  // The legacy "profile" (Linktree) mode is retired but kept in the type so
  // existing saved configs still load without errors.
  mode: "profile" | "direct";
  title: string;
  bio: string;
  backgroundImage: string | null;
  primaryColor: string;
  // Minimal payment-page options (image-free).
  buttonText: string;
  minAmount: number;
  maxAmount: number;
  // Legacy profile-mode fields — retained for backward compatibility only.
  showDonation: boolean;
  showPayment: boolean;
  showCoffee: boolean;
  donationLabel: string;
  paymentLabel: string;
  coffeeLabel: string;
}

export const DEFAULT_UNIVERSAL_CONFIG: UniversalLinkConfig = {
  mode: "direct",
  title: "",
  bio: "",
  backgroundImage: null,
  primaryColor: "#61CE70",
  buttonText: "Top Up Now",
  minAmount: 5,
  maxAmount: 500,
  showDonation: true,
  showPayment: true,
  showCoffee: true,
  donationLabel: "Donation Box",
  paymentLabel: "Send Payment",
  coffeeLabel: "Support Tips Jar",
};

interface UniversalLinkPreviewProps {
  config: UniversalLinkConfig;
  ownerUsername: string;
  ownerPhotoURL?: string | null;
  collectSettings?: {
    donationBoxEnabled?: boolean;
    requestPaymentEnabled?: boolean;
    buyMeCoffeeEnabled?: boolean;
  };
  disabledPaymentMethods?: Record<string, string[]>;
}

export default function UniversalLinkPreview({
  config,
  ownerUsername,
  ownerPhotoURL,
  collectSettings,
  disabledPaymentMethods = {},
}: UniversalLinkPreviewProps) {
  const color = config.primaryColor || "#61CE70";
  const title = config.title || ownerUsername || "Your Name";
  const bio = config.bio || "Welcome to my page";

  const links = [
    {
      key: "payment",
      label: config.paymentLabel || "Send Payment",
      icon: <Wallet size={20} />,
      show:
        config.showPayment && collectSettings?.requestPaymentEnabled !== false,
      gradient: "from-emerald-500 to-teal-600",
    },
    {
      key: "donation",
      label: config.donationLabel || "Donation Box",
      icon: <Heart size={20} />,
      show:
        config.showDonation && collectSettings?.donationBoxEnabled !== false,
      gradient: "from-pink-500 to-rose-600",
    },
    {
      key: "coffee",
      label: config.coffeeLabel || "Support Tips Jar",
      icon: <Gift size={20} />,
      show: config.showCoffee && collectSettings?.buyMeCoffeeEnabled !== false,
      gradient: "from-amber-400 to-orange-500",
    },
  ].filter((l) => l.show);

  // Direct mode preview — minimal, single-card payment page.
  if (config.mode === "direct") {
    const heading = config.title || "Secure Payment";
    const subheading = config.bio || "Complete your transaction safely";
    const ctaLabel = config.buttonText || "Top Up Now";
    const requestDisabled = disabledPaymentMethods["request"] || [];
    const previewMethods = ALL_PAYMENT_METHOD_IDS.filter(
      (id) =>
        id !== "card-bank" &&
        !HIDDEN_PAYMENT_METHOD_IDS.includes(id) && // peer extension down
        // Beta-only methods (Coinbase Pay / PayPal / Venmo) are gated behind
        // `!isPublic && isBetaUser` in PaymentFlow, so they never render on a
        // public payment link. Keep the preview faithful to the real page.
        !PREVIEW_BETA_ONLY_IDS.includes(id) &&
        !requestDisabled.includes(id),
    ).slice(0, 5);

    return (
      <div className="w-full">
        <div className="mx-auto max-w-[400px]">
          <div className="rounded-3xl overflow-hidden border border-gray-700/50 shadow-2xl shadow-black/40 bg-gradient-to-br from-[#0F1419] to-[#1A1F2B]">
            {/* Green header */}
            <div
              className="px-5 py-4 flex items-center gap-3"
              style={{
                background: `linear-gradient(135deg, ${color}, ${color}cc)`,
              }}
            >
              <div className="w-10 h-10 rounded-2xl bg-white/20 flex items-center justify-center shrink-0">
                <ShieldCheck size={20} className="text-white" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-white font-bold text-sm leading-tight truncate">
                  {heading}
                </p>
                <p className="text-white/80 text-[11px] leading-tight truncate">
                  {subheading}
                </p>
              </div>
              <img src="/logo.png" alt="CashTap" className="h-6 w-auto opacity-90 shrink-0" />
            </div>

            {/* Body */}
            <div className="p-5 space-y-4">
              {/* Email */}
              <div>
                <p className="text-gray-500 text-[10px] font-semibold uppercase tracking-wider mb-1.5">
                  Email Address
                </p>
                <div className="h-10 rounded-xl bg-gray-800/40 border border-gray-700/50 flex items-center px-3">
                  <span className="text-gray-500 text-xs">you@example.com</span>
                </div>
              </div>

              {/* Amount */}
              <div>
                <p className="text-gray-500 text-[10px] font-semibold uppercase tracking-wider mb-1.5">
                  Amount (USD)
                </p>
                <div className="h-11 rounded-xl bg-gray-800/40 border border-gray-700/50 flex items-center px-3">
                  <span className="text-gray-400 text-lg mr-1">$</span>
                  <span className="text-white font-semibold text-lg">0.00</span>
                </div>
                <p className="text-gray-500 text-[10px] mt-1.5">
                  *Minimum ${config.minAmount} - Maximum ${config.maxAmount}
                </p>
              </div>

              {/* Methods */}
              {previewMethods.length > 0 && (
                <div>
                  <p className="text-white font-semibold text-xs mb-2">
                    Select Payment Method
                  </p>
                  <div className="space-y-1.5">
                    {previewMethods.map((id, idx) => (
                      <div
                        key={id}
                        className={`flex items-center gap-2.5 px-3 py-2 rounded-xl border transition-all ${
                          idx === 0
                            ? "border-[#61CE70]/50 bg-[#61CE70]/5"
                            : "border-gray-700/40 bg-gray-800/20"
                        }`}
                      >
                        <div className="shrink-0">{METHOD_LOGOS[id]}</div>
                        <span
                          className={`text-[11px] font-medium ${idx === 0 ? "text-white" : "text-gray-400"}`}
                        >
                          {METHOD_LABEL_OVERRIDES[id] ?? PAYMENT_METHOD_LABELS[id]}
                        </span>
                        {idx === 0 ? (
                          <div className="ml-auto w-3 h-3 rounded-full border-2 border-[#61CE70] bg-[#61CE70] flex items-center justify-center">
                            <div className="w-1 h-1 rounded-full bg-white" />
                          </div>
                        ) : (
                          <div className="ml-auto w-3 h-3 rounded-full border-2 border-gray-600" />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* CTA */}
              <div
                className="text-center py-3 rounded-xl text-sm font-bold text-white shadow-lg"
                style={{ backgroundColor: color }}
              >
                {ctaLabel}
              </div>

              {/* Footer */}
              <div className="flex items-center justify-center gap-1.5 pt-1 opacity-50">
                <img src="/logo.png" alt="CashTap" className="h-4 w-auto" />
                <span className="text-gray-500 text-[10px]">
                  Pay Securely With CashTap
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Profile / Linktree mode preview
  return (
    <div className="w-full">
      <div className="mx-auto max-w-[420px]">
        <div className="rounded-[2.5rem] overflow-hidden border-[6px] border-gray-700/60 shadow-2xl shadow-black/40 bg-[#090D14]">
          {/* Notch */}
          <div className="relative bg-[#090D14]">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-28 h-5 bg-[#090D14] rounded-b-2xl z-10" />
          </div>

          <div className="bg-gradient-to-br from-[#090D14] via-[#0A0E15] to-[#0B0F16] min-h-[580px]">
            {/* Cover image */}
            <div className="relative">
              <div className="overflow-hidden">
                {config.backgroundImage ? (
                  <div className="relative">
                    <img
                      src={config.backgroundImage}
                      alt="Cover"
                      className="w-full h-36 object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#090D14] via-[#090D14]/50 to-transparent" />
                  </div>
                ) : (
                  <div
                    className="w-full h-36"
                    style={{
                      background: `linear-gradient(135deg, ${color}30, ${color}10, #090D14)`,
                    }}
                  />
                )}
              </div>

              {/* Avatar overlapping */}
              <div className="absolute -bottom-10 left-1/2 -translate-x-1/2 z-10">
                <div className="w-20 h-20 rounded-full overflow-hidden border-[3px] border-[#090D14] shadow-lg">
                  {ownerPhotoURL ? (
                    <img
                      src={ownerPhotoURL}
                      alt={ownerUsername}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div
                      className="w-full h-full flex items-center justify-center"
                      style={{
                        background: `linear-gradient(135deg, ${color}33, ${color}11)`,
                      }}
                    >
                      <span className="text-2xl font-bold text-white/80">
                        {(ownerUsername || "?")[0]?.toUpperCase()}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Spacer */}
            <div className="h-12" />

            {/* Name & bio */}
            <div className="text-center px-6 mb-6">
              <h2 className="text-white font-bold text-xl mb-1">{title}</h2>
              <p className="text-gray-400 text-sm leading-relaxed line-clamp-3">
                {bio}
              </p>
            </div>

            {/* Links */}
            <div className="px-5 space-y-3 pb-8">
              {links.map((link) => (
                <div
                  key={link.key}
                  className="group relative overflow-hidden rounded-2xl border border-gray-700/40 bg-gradient-to-r from-gray-800/40 to-gray-800/20 hover:border-gray-600/60 transition-all cursor-pointer"
                >
                  <div className="flex items-center gap-4 px-5 py-4">
                    <div
                      className={`w-10 h-10 rounded-xl bg-gradient-to-br ${link.gradient} flex items-center justify-center text-white shadow-lg shrink-0`}
                    >
                      {link.icon}
                    </div>
                    <span className="text-white font-semibold text-sm flex-1">
                      {link.label}
                    </span>
                    <svg
                      className="w-4 h-4 text-gray-500 group-hover:text-gray-300 transition-colors"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </div>
                </div>
              ))}

              {links.length === 0 && (
                <p className="text-gray-500 text-sm text-center py-6">
                  No payment options enabled
                </p>
              )}

              {/* Select Payment Method */}
              {links.length > 0 && (() => {
                const typesToCheck = [
                  ...(config.showPayment && collectSettings?.requestPaymentEnabled !== false ? ["request"] : []),
                  ...(config.showDonation && collectSettings?.donationBoxEnabled !== false ? ["donation"] : []),
                  ...(config.showCoffee && collectSettings?.buyMeCoffeeEnabled !== false ? ["coffee"] : []),
                ];
                const enabledMethods = ALL_PAYMENT_METHOD_IDS
                  .filter((id) => id !== "card-bank") // Stripe not available on collect pages
                  .filter((id) => !HIDDEN_PAYMENT_METHOD_IDS.includes(id)) // peer extension down
                  .filter((id) =>
                    typesToCheck.some((t) => !(disabledPaymentMethods[t] || []).includes(id))
                  );
                return enabledMethods.length > 0 ? (
                  <div className="mt-4 pt-4 border-t border-gray-700/30">
                    <p className="text-white font-semibold text-xs mb-2">Select Payment Method</p>
                    <div className="space-y-1.5">
                      {enabledMethods.map((id, idx) => (
                        <div
                          key={id}
                          className={`flex items-center gap-2.5 px-3 py-2 rounded-xl border transition-all ${
                            idx === 0
                              ? "border-[#61CE70]/50 bg-[#61CE70]/5"
                              : "border-gray-700/40 bg-gray-800/20"
                          }`}
                        >
                          <div className="shrink-0">{METHOD_LOGOS[id]}</div>
                          <span className={`text-[11px] font-medium ${idx === 0 ? "text-white" : "text-gray-400"}`}>
                            {METHOD_LABEL_OVERRIDES[id] ?? PAYMENT_METHOD_LABELS[id]}
                          </span>
                          {idx === 0 ? (
                            <div className="ml-auto w-3 h-3 rounded-full border-2 border-[#61CE70] bg-[#61CE70] flex items-center justify-center">
                              <div className="w-1 h-1 rounded-full bg-white" />
                            </div>
                          ) : (
                            <div className="ml-auto w-3 h-3 rounded-full border-2 border-gray-600" />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null;
              })()}
            </div>

            {/* Footer */}
            <div className="pb-8">
              <div className="flex items-center justify-center gap-2 opacity-40">
                <img src="/logo.png" alt="CashTap" className="h-5 w-auto" />
                <span className="text-gray-500 text-[10px]">
                  Powered by CashTap
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
